import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Flex } from "../../../../styled-system/jsx";
import { TextField } from "../../../components/fields/base/TextField";
import { Button } from "../../../components/core/basic/Button";
import { css } from "../../../../styled-system/css";
import { useSetPassword } from "../../auth/api/useSetPassword";
import { successToast } from "../../../utils/toasts";
import { isAxiosError } from "axios";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { useCurrentUserProfile } from "../../auth/api/useCurrentUserProfile";

const ChangePasswordForm = ({ closeForm }: { closeForm: () => void }) => {
  const { t } = useTranslation(["settings", "common", "validation"]);
  const { data } = useCurrentUserProfile();
  const isSocialAuthUser = data?.login_method === "social_auth";

  const { control, handleSubmit, setError } = useForm<{
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const updatePasswordMutation = useSetPassword();

  const onSubmit = (data: { currentPassword: string; newPassword: string; confirmNewPassword: string }) => {
    if (data.newPassword !== data.confirmNewPassword) {
      setError("confirmNewPassword", {
        type: "manual",
        message: t(($) => $.settings.changePassword["New password and confirmation do not match."]),
      });
      return;
    }

    updatePasswordMutation.mutate(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          successToast({ message: t(($) => $.settings.changePassword["Password successfully updated."]) });
          closeForm();
        },
        onError: (error) => {
          let errorMessage = t(($) => $.settings.changePassword["Failed to set new password, please retry."]);
          if (isAxiosError(error)) {
            errorMessage = error.response?.data?.error ?? errorMessage;
          }
          if (errorMessage.toLowerCase().includes("current password")) {
            setError("currentPassword", { type: "manual", message: errorMessage });
            return;
          }
          setError("newPassword", { type: "manual", message: errorMessage });
        },
      },
    );
  };

  return (
    <>
      <p className={css({ paddingY: "small", fontSize: "medium" })}>
        {t(($) => $.settings.changePassword["Update your password using the form below."])}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className={css({ width: "400px" })}>
        <Flex gap={"medium"} direction={"column"}>
          <Controller
            name={"currentPassword"}
            control={control}
            rules={{
              required: {
                value: !isSocialAuthUser,
                message: t(($) => $.validation.field["This field is required"]),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={t(($) => $.settings.changePassword["Current Password"])}
                type={"password"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                isDisabled={isSocialAuthUser}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
                description={
                  isSocialAuthUser ?
                    t(
                      ($) =>
                        $.settings.changePassword[
                          "This field is only available to users who sign in with an email and password."
                        ],
                    )
                  : undefined
                }
              />
            )}
          />
          <Controller
            name={"newPassword"}
            control={control}
            rules={{
              required: {
                value: true,
                message: t(($) => $.validation.field["This field is required"]),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={
                  <span className={css({ display: "flex", gap: "small" })}>
                    {t(($) => $.settings.changePassword["New Password"])}{" "}
                    <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                      {t(
                        ($) =>
                          $.settings.changePassword[
                            "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number."
                          ],
                      )}
                    </HoverInfo>
                  </span>
                }
                type={"password"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name={"confirmNewPassword"}
            control={control}
            rules={{
              required: {
                value: true,
                message: t(($) => $.validation.field["This field is required"]),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={t(($) => $.settings.changePassword["Confirm New Password"])}
                type={"password"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Flex gap={"xlarge"} justifyContent={"center"}>
            <Button
              variant={"secondary"}
              onPress={() => {
                closeForm();
              }}
            >
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button type={"submit"} isPending={updatePasswordMutation.isPending}>
              {t(($) => $.settings.changePassword["Update Password"])}
            </Button>
          </Flex>
        </Flex>
      </form>
    </>
  );
};

export default ChangePasswordForm;
